import axios from 'axios';
import { useAuthStore } from '@/stores/authStore';
import { useBillingStore, type InsufficientTokensDetail } from '@/stores/billingStore';

export const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '',
  // Exports can render heavy PDFs server-side; bump the timeout so we don't
  // bail before the file finishes streaming. Most calls finish in <500ms.
  timeout: 60_000,
});

api.interceptors.request.use((config) => {
  const token = useAuthStore.getState().token;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

/** Convert axios blob responses to parsed JSON so we can read 402 payloads. */
async function readBlobAsJson(blob: Blob): Promise<unknown> {
  try {
    const text = await blob.text();
    return JSON.parse(text);
  } catch {
    return null;
  }
}

api.interceptors.response.use(
  (response) => {
    // Saldo en tiempo real (v3): el backend adjunta X-Tokens-Balance y
    // X-Subscription-Status en cada respuesta autenticada. Si el saldo
    // cambió respecto al cache local, parchamos el store sin recargar.
    const newBalanceStr =
      response.headers?.['x-tokens-balance']
      ?? response.headers?.['X-Tokens-Balance'];
    const newSubStatus =
      response.headers?.['x-subscription-status']
      ?? response.headers?.['X-Subscription-Status'];
    if (newBalanceStr != null) {
      const newBalance = Number(newBalanceStr);
      const auth = useAuthStore.getState();
      if (
        Number.isFinite(newBalance)
        && auth.user
        && auth.user.tokens_balance !== newBalance
      ) {
        auth.patchBilling({ tokens_balance: newBalance });
      }
    }
    if (typeof newSubStatus === 'string') {
      const auth = useAuthStore.getState();
      if (auth.user && auth.user.subscription_status !== newSubStatus) {
        auth.patchBilling({ subscription_status: newSubStatus });
      }
    }
    return response;
  },
  async (error) => {
    if (error.response?.status === 401) {
      // session expired or invalid -> drop it so the user is bounced to /login
      const store = useAuthStore.getState();
      if (store.token) store.logout();
    }
    if (error.response?.status === 402) {
      // Insufficient tokens. The payload is a FastAPI `detail` dict, but if
      // the request was launched with `responseType: 'blob'` (export buttons),
      // axios parses the body as a Blob — we have to read+parse it ourselves.
      let detail: unknown = error.response.data?.detail ?? error.response.data;
      if (detail instanceof Blob) {
        const parsed = await readBlobAsJson(detail);
        detail = (parsed as { detail?: unknown })?.detail ?? parsed;
      }
      if (
        detail &&
        typeof detail === 'object' &&
        'error' in detail &&
        (detail as { error: string }).error === 'insufficient_tokens'
      ) {
        useBillingStore.getState().openInsufficient(detail as InsufficientTokensDetail);
      }
    }
    if (error.response) return Promise.reject(error);
    if (error.request) return Promise.reject(new Error('No se pudo contactar al servidor'));
    return Promise.reject(error);
  },
);
